import type { FinancialSyncStatusResponse } from "@features/api/FinancialSyncStatusApi";

/**
 * Completion contract (EDG-529): sync is finished only when the backend
 * reports summary=COMPLETE over a non-empty total that is fully covered.
 * A COMPLETE with synced_count < total_count is NOT done (legacy mirrors
 * only count accounts that emitted an event) — keep waiting; the generate
 * chain's 409 ACCOUNT_DATA_NOT_READY retry remains the backend backstop.
 */
export function isSyncStatusDone(
  status: FinancialSyncStatusResponse | null | undefined,
): boolean {
  return Boolean(
    status &&
    status.summary === "COMPLETE" &&
    status.total_count > 0 &&
    status.synced_count === status.total_count,
  );
}

export interface SyncProgressView {
  /**
   * - "progress": still syncing with a known denominator → show synced/total
   * - "waiting": no event data yet (IDLE / zero total / API absent) → keep
   *   the static waiting copy, never report completion
   * - "done": completion contract satisfied → no progress line
   */
  kind: "progress" | "waiting" | "done";
  synced?: number;
  total?: number;
  /** Count of FAILED accounts visible in the mirror (all-accounts; display shows the full picture — product ruling keeps the denominator un-scoped). */
  failedCount?: number;
}

/**
 * Pure view resolver shared by the LoaderStep progress title and the F9
 * pending progress line. Kept dependency-free so tests can mock the polling
 * hook without pulling the real provider chain.
 */
export function resolveSyncProgressView(
  status: FinancialSyncStatusResponse | null | undefined,
): SyncProgressView {
  if (isSyncStatusDone(status)) {
    return { kind: "done" };
  }
  if (!status || status.summary === "IDLE" || status.total_count <= 0) {
    return { kind: "waiting" };
  }
  const accounts = Array.isArray(status.accounts) ? status.accounts : [];
  return {
    kind: "progress",
    synced: status.synced_count,
    total: status.total_count,
    failedCount: accounts.filter((a) => a.state === "FAILED").length,
  };
}

/** Account-type match for the invest-only generate gate (INVESTMENT et al). */
export function isInvestAccountType(accountType: string | undefined): boolean {
  return (accountType ?? "").toUpperCase().includes("INVEST");
}

/** True when the mirror exposes account_type (deployed backend); false → gate falls back to all-accounts completion. */
export function syncAccountTypesKnown(
  status: FinancialSyncStatusResponse | null | undefined,
): boolean {
  const accounts = Array.isArray(status?.accounts) ? status.accounts : [];
  return Boolean(
    status &&
    accounts.length > 0 &&
    accounts.some((a) => typeof a.account_type === "string"),
  );
}

export type SyncGateView =
  /** No account_type info (undeployed backend) → legacy all-accounts completion gate. */
  | { kind: "unknown" }
  /** Directory known: zero investment accounts → invest goal should not wait for generate. */
  | { kind: "no-invest" }
  /** Investment accounts present, some not COMPLETE yet — bank accounts never block.
   *  FAILED invest accounts are terminal and excluded from the denominator
   *  (EDG-529: a failed account never becomes COMPLETE; waiting on it is dead wait). */
  | { kind: "invest-progress"; investSynced: number; investTotal: number }
  /** Every non-failed investment account COMPLETE (banks may still sync — display keeps its own all-accounts denominator). */
  | { kind: "invest-done" }
  /** Every investment account reached a terminal state with none COMPLETE — waiting is pointless; caller should fail fast. */
  | { kind: "invest-failed"; failedCount: number };

/** One end-to-end wait budget shared by the loader UI and generate gate. */
export const FINANCIAL_SYNC_WAIT_BUDGET_MS = 60_000;

/**
 * Invest-only generate gate (EDG-529 product ruling): the onboarding invest
 * path needs real holdings from INVESTMENT accounts only. Truth table:
 * - types known && all invest COMPLETE → invest-done (release generate even
 *   while bank accounts still sync)
 * - types known && invest accounts present, some pending → invest-progress
 *   (keep waiting; banks never block)
 * - types known && zero invest accounts → no-invest (caller routes the user
 *   away from the invest goal instead of calling generate)
 * - no account_type info → unknown (caller falls back to isSyncStatusDone,
 *   the all-accounts completion contract — fail-safe against undeployed
 *   backends)
 */
export function resolveSyncGateView(
  status: FinancialSyncStatusResponse | null | undefined,
): SyncGateView {
  if (!syncAccountTypesKnown(status) || !status) {
    return { kind: "unknown" };
  }
  const accounts = Array.isArray(status.accounts) ? status.accounts : [];
  const invest = accounts.filter((a) => isInvestAccountType(a.account_type));
  if (invest.length === 0) {
    return { kind: "no-invest" };
  }
  const completed = invest.filter((a) => a.state === "COMPLETE");
  const failed = invest.filter((a) => a.state === "FAILED");
  const pending = invest.filter(
    (a) => a.state !== "COMPLETE" && a.state !== "FAILED",
  );
  // Terminal-failure fast path (EDG-529 gap ①): all invest accounts are
  // terminal and none succeeded — waiting the full window is dead wait.
  if (pending.length === 0 && completed.length === 0 && failed.length > 0) {
    return { kind: "invest-failed", failedCount: failed.length };
  }
  // Exclude FAILED from the denominator: partial availability beats none;
  // the failed account surfaces via the report's coverage statement.
  if (pending.length === 0) {
    return { kind: "invest-done" };
  }
  return {
    kind: "invest-progress",
    investSynced: completed.length,
    investTotal: completed.length + pending.length,
  };
}
